import React, { useState, useEffect } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import "./VehicleEmiDueReport.css";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { toast } from "react-toastify";

const VehicleEmiDueReport = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);

  const [centers, setCenters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [dayFilter, setDayFilter] = useState("");

  const [emiData, setEmiData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [company, setCompany] = useState("");

  const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

  const companyName = company?.name || profile?.branchName || "Company";

  const selectedCenterName =
    centers.find((c) => c.id == selectedCenter)?.CenterName || "All";

  const selectedGroupName =
    groups.find((g) => g.id == selectedGroup)?.groupname || "All";

  const selectedFilterName = dayFilter || "All";

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const fetchEmis = async (page = 1) => {
    if (!profile?.branchId) return;

    try {
      setLoading(true);

      const res = await axios.get(
        "https://payyug.in/api/gold-loan/due-report/all",
        {
          params: {
            superAdminId: profile.superAdminId,
            page,
            limit: itemsPerPage,
          },
        }
      );

      if (res.data?.success) {
        setEmiData(res.data.data || []);
        setCurrentPage(res.data.currentPage);
        setTotalPages(res.data.totalPages);
        setTotalRecords(res.data.totalRecords);
      }
    } catch (error) {
      console.error("EMI fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmis(currentPage);
  }, [profile, currentPage, itemsPerPage]);

  const filteredData = emiData.filter((r) => {
    const text = `
      ${r.loanAccount?.loanAccountNumber}
      ${r.loanAccount?.memberName}
      ${r.loanAccount?.centerName}
      ${r.loanAccount?.groupName}
    `.toLowerCase();

    return text.includes(searchQuery.toLowerCase());
  });

  const handleFilter = () => {
    if (!selectedCenter || !selectedGroup) {
      toast.warn("Please select center and group");
      return;
    }

    setCurrentPage(1);
    fetchEmis(1);
  };

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${profile?.superAdminId}`
        );
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo)
            data.logoUrl = `https://payyug.in/${data.logo.replace(
              /^images\//,
              ""
            )}`;
          setCompany(data);
        }
      } catch (err) {
        console.error("Failed to fetch company details", err);
      }
    };

    fetchCompany();
  }, [profile?.superAdminId]);

  const exportCSV = () => {
    const headerInfo = `
Company: ${companyName}
Address: ${company?.address}
Email: ${company.email}
Contact: ${company?.contact}
Center: ${selectedCenterName}
Group: ${selectedGroupName}
Filter: ${selectedFilterName}

`;

    const tableHeader =
      "Account No,Member,Group,EMI Amount,Due Date,Status\n";

    const tableData = emiData
      .map((r) => {
        const status = r.paidDate
          ? "Paid"
          : new Date(r.dueDate) < new Date()
          ? "Overdue"
          : "Pending";

        return [
          r.loanAccount?.loanAccountNumber,
          r.loanAccount?.memberName,
          r.loanAccount?.groupName,
          r.emiAmount,
          new Date(r.dueDate).toLocaleDateString("en-GB"),
          status,
        ].join(",");
      })
      .join("\n");

    const csvContent = headerInfo + tableHeader + tableData;

    saveAs(new Blob([csvContent]), "vehicle_loan_emi_due_report.csv");
  };

  const exportExcel = () => {
    const reportData = [
      ["Company", companyName],
      ["Address", company?.address],
      ["Contact", company?.contact],
      ["Email", company?.email],
      ["Center", selectedCenterName],
      ["Group", selectedGroupName],
      ["Filter", selectedFilterName],
      [],
      ["Account No", "Member", "Group", "EMI", "Due Date", "Status"],
      ...emiData.map((r) => {
        const status = r.paidDate
          ? "Paid"
          : new Date(r.dueDate) < new Date()
          ? "Overdue"
          : "Pending";

        return [
          r.loanAccount?.loanAccountNumber,
          r.loanAccount?.memberName,
          r.loanAccount?.groupName,
          r.emiAmount,
          new Date(r.dueDate).toLocaleDateString("en-GB"),
          status,
        ];
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicle Loan EMI Report");
    XLSX.writeFile(wb, "vehicle_loan_emi_due_report.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const todayDate = new Date().toLocaleDateString("en-GB");

    doc.setFillColor(212, 175, 55);
    doc.rect(0, 0, pageWidth, 110, "F");

    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(companyName?.toUpperCase() || "", 40, 35);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    doc.text(company?.address || "", 40, 55, { maxWidth: 500 });
    doc.text(`Email: ${company?.email || ""}`, 40, 70);
    doc.text(`Contact: ${company?.contact || ""}`, 40, 82);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("VEHICLE LOAN EMI DUE REPORT", 40, 135);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    doc.text(`Center : ${selectedCenterName}`, 40, 160);
    doc.text(`Group  : ${selectedGroupName}`, 40, 175);
    doc.text(`Filter : ${selectedFilterName}`, 40, 190);

    doc.text(`Generated On : ${todayDate}`, 350, 160);
    doc.text(`Total Records : ${emiData.length}`, 350, 175);

    autoTable(doc, {
      startY: 210,
      head: [
        [
          "Account No",
          "Member",
          "Group",
          "Loan Amt",
          "EMI",
          "Due Date",
          "Status",
        ],
      ],
      body: emiData.map((r) => {
        const status = r.paidDate
          ? "Paid"
          : new Date(r.dueDate) < new Date()
          ? "Overdue"
          : "Pending";

        return [
          r.loanAccount?.loanAccountNumber,
          r.loanAccount?.memberName,
          r.loanAccount?.groupName,
          r.loanAccount?.loanAmount,
          r.emiAmount,
          new Date(r.dueDate).toLocaleDateString("en-GB"),
          status,
        ];
      }),
    });

    doc.save("vehicle_loan_emi_due_report.pdf");
  };
  
  return (
    <div className="vldr-container">
      <h1 className="vldr-heading">
        <i className="bi bi-file-earmark-text"></i> Vehicle Loan EMI Due Report
      </h1>

      {filteredData.length > 0 && (
        <div className="vldr-pdf-download-btns">
          <button onClick={exportCSV}>
            <Download size={15} /> CSV
          </button>
          <button onClick={exportExcel}>
            <Download size={15} /> Excel
          </button>
          <button onClick={exportPDF}>
            <Download size={15} /> PDF
          </button>
        </div>
      )}

      <div className="vldr-report-filter">
        <input
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />

        <div className="vldr-report-filter-inputs">
          <label>Show </label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span> entries</span>
        </div>
      </div>

      <div className="vldr-report-table-container">
        <table className="vldr-report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Loan Account No</th>
              <th>Loan Amount</th>
              <th>Member</th>
              <th>EMI Number</th>
              <th>EMI Amount</th>
              <th>Due Date</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" align="center">
                  Loading...
                </td>
              </tr>
            ) : filteredData.length ? (
              filteredData.map((r, i) => (
                <tr key={r.id}>
                  <td>{(currentPage - 1) * itemsPerPage + i + 1}</td>
                  <td>{r?.loanAccountNumber || "N/A"}</td>
                  <td>₹{r?.outstanding || "N/A"} </td>
                  <td>{r?.memberName || "N/A"} </td>
                  <td>{r?.emiNumber || "N/A"}</td>
                  <td>₹{r.emiAmount}</td>
                  <td>{new Date(r.dueDate).toLocaleDateString("en-GB")}</td>
                  <td
                    className={
                      r.status.toLowerCase() === "paid"
                        ? "status-paid"
                        : r.status.toLowerCase() === "pending"
                        ? "status-pending"
                        : "status-overdue"
                    }
                    style={{ textTransform: "capitalize" }}
                  >
                    {r.status.toLowerCase()}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" align="center">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="vldr-pagination">
        <button
          disabled={currentPage === 1 || filteredData.length === 0}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          ⬅ Prev
        </button>

        <span>
          {filteredData.length === 0
            ? "No data"
            : `Page ${currentPage} of ${totalPages}`}
        </span>

        <button
          disabled={currentPage === totalPages || filteredData.length === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next ➡
        </button>
      </div>
    </div>
  );
};

export default VehicleEmiDueReport;